import React from 'react';
import {
  TextField,
  Button,
  Flex,
  Text,
  TooltipTrigger,
  Tooltip,
  Switch,
  Checkbox,
} from '@chatsdet/ui';
import { Plus, Trash, Check, Warning } from '@phosphor-icons/react';
import type { SeedDataOutput, CredentialOutputConfig } from '../../hooks/useSeedWorkflowOutputs';
import { useAuth } from '../../contexts/AuthContext';

type SeedOutputsEditorProps = {
  outputs: SeedDataOutput[];
  onChange: (outputs: SeedDataOutput[]) => void;
  /** Set of dataKeys that have been produced by a seed run. When provided, shows validation status per output. */
  producedKeys?: Set<string>;
};

export function SeedOutputsEditor({ outputs, onChange, producedKeys }: SeedOutputsEditorProps) {
  const { user } = useAuth();
  const ephemeralEmailDomain = user?.ephemeralEmailDomain ?? 'testuser.runcofactor.com';

  const addOutput = () => {
    onChange([...outputs, { dataKey: '', name: '', displayLabel: '' }]);
  };

  const updateOutput = (index: number, patch: Partial<SeedDataOutput>) => {
    const updated = [...outputs];
    const current = updated[index];
    const merged = { ...current, ...patch };
    // Auto-default displayLabel to name
    if (
      patch.name !== undefined &&
      (!current.displayLabel || current.displayLabel === current.name)
    ) {
      merged.displayLabel = patch.name;
    }
    updated[index] = merged;
    onChange(updated);
  };

  const removeOutput = (index: number) => {
    onChange(outputs.filter((_, i) => i !== index));
  };

  const toggleCredentialOutput = (index: number, enabled: boolean) => {
    if (enabled) {
      updateOutput(index, {
        credentialOutput: {
          fieldMapping: { username: '$generate.email', password: '' },
          emailPrefix: outputs[index].dataKey || 'testuser',
          createInboxRecipient: true,
        },
      });
    } else {
      updateOutput(index, { credentialOutput: undefined });
    }
  };

  const updateCredentialOutput = (index: number, patch: Partial<CredentialOutputConfig>) => {
    const current = outputs[index].credentialOutput;
    if (!current) return;
    updateOutput(index, { credentialOutput: { ...current, ...patch } });
  };

  return (
    <div className="space-y-3">
      {outputs.map((output, index) => {
        const isProduced = producedKeys ? producedKeys.has(output.dataKey) : undefined;
        const hasCredential = !!output.credentialOutput;

        return (
          <div key={index} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <Flex align="center" justify="between">
              <Flex align="center" gap="sm">
                <Text size="xs" weight="medium" className="text-muted-foreground">
                  Output {index + 1}
                </Text>
                {isProduced !== undefined && output.dataKey && (
                  <TooltipTrigger delay={200}>
                    <span className="inline-flex cursor-default">
                      {isProduced ? (
                        <Check size={14} weight="bold" className="text-green-500" />
                      ) : (
                        <Warning size={14} weight="fill" className="text-amber-500" />
                      )}
                    </span>
                    <Tooltip>
                      {isProduced
                        ? `"${output.dataKey}" produced by last seed run`
                        : `"${output.dataKey}" not yet produced — run the seed script to populate`}
                    </Tooltip>
                  </TooltipTrigger>
                )}
              </Flex>
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Remove output"
                onPress={() => removeOutput(index)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash size={14} />
              </Button>
            </Flex>

            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Name"
                aria-label="Output name"
                value={output.name}
                onChange={(value) => updateOutput(index, { name: value })}
                placeholder="Project ID"
                isRequired
                errorMessage={
                  hasCredential && !output.name
                    ? 'Name is required when credential provisioning is enabled'
                    : undefined
                }
              />
              <TextField
                label="Data Key"
                aria-label="Data key"
                value={output.dataKey}
                onChange={(value) => updateOutput(index, { dataKey: value })}
                placeholder="project_id"
                className="font-mono text-sm"
                isRequired
                errorMessage={
                  hasCredential && !output.dataKey
                    ? 'Data key is required when credential provisioning is enabled'
                    : undefined
                }
              />
            </div>

            <TextField
              label="Description"
              aria-label="Output description"
              value={output.description || ''}
              onChange={(value) => updateOutput(index, { description: value || undefined })}
              placeholder="ID of the seeded project"
            />

            {/* Credential output toggle */}
            <div className="border-t border-border pt-3">
              <Switch
                isSelected={hasCredential}
                onChange={(checked) => toggleCredentialOutput(index, checked)}
                label="Provisions a credential"
              />

              {hasCredential && output.credentialOutput && (
                <div className="mt-3 space-y-3 pl-1">
                  <div className="grid grid-cols-2 gap-3">
                    <TextField
                      label="Username"
                      aria-label="Username value or token"
                      value={output.credentialOutput.fieldMapping?.username ?? ''}
                      onChange={(value) =>
                        updateCredentialOutput(index, {
                          fieldMapping: {
                            ...output.credentialOutput!.fieldMapping,
                            username: value,
                          },
                        })
                      }
                      placeholder="$generate.email"
                      description="Use $generate.email for auto-generated email. Available in API sequences as $var.credential.username"
                    />
                    <TextField
                      label="Password"
                      aria-label="Password value"
                      value={output.credentialOutput.fieldMapping?.password ?? ''}
                      onChange={(value) =>
                        updateCredentialOutput(index, {
                          fieldMapping: {
                            ...output.credentialOutput!.fieldMapping,
                            password: value,
                          },
                        })
                      }
                      placeholder="TestPassword123!"
                    />
                  </div>

                  <TextField
                    label="Email Prefix"
                    aria-label="Email prefix"
                    value={output.credentialOutput.emailPrefix || ''}
                    onChange={(value) =>
                      updateCredentialOutput(index, { emailPrefix: value || undefined })
                    }
                    placeholder="claimant"
                    description={`Generated email: {prefix}+{execId}@${ephemeralEmailDomain}`}
                  />

                  <TextField
                    label="Login URL"
                    aria-label="Login URL"
                    value={output.credentialOutput.loginUrl || ''}
                    onChange={(value) =>
                      updateCredentialOutput(index, { loginUrl: value || undefined })
                    }
                    placeholder="https://app.example.com/login"
                  />

                  <TextField
                    label="Login Instructions"
                    aria-label="Login instructions"
                    value={output.credentialOutput.loginInstructions || ''}
                    onChange={(value) =>
                      updateCredentialOutput(index, { loginInstructions: value || undefined })
                    }
                    placeholder="Navigate to the login page and enter credentials"
                  />

                  <Checkbox
                    isSelected={output.credentialOutput.createInboxRecipient ?? false}
                    onChange={(checked) =>
                      updateCredentialOutput(index, { createInboxRecipient: checked })
                    }
                    label="Create email inbox"
                    description="Auto-create an inbox recipient to receive emails at the generated address"
                  />

                  {user?.features?.smsAgentMessaging && (
                    <Checkbox
                      isSelected={output.credentialOutput.provisionPhoneNumber ?? false}
                      onChange={(checked) =>
                        updateCredentialOutput(index, {
                          provisionPhoneNumber: checked,
                          fieldMapping: checked
                            ? {
                                ...output.credentialOutput!.fieldMapping,
                                phone:
                                  output.credentialOutput!.fieldMapping.phone || '$generate.phone',
                              }
                            : output.credentialOutput!.fieldMapping,
                        })
                      }
                      label="Provision SMS phone number"
                      description="Reserve a managed US number and expose it as $var.credential.phone"
                    />
                  )}

                  <Checkbox
                    isSelected={output.credentialOutput.provisioningInstructions !== undefined}
                    onChange={(checked) =>
                      updateCredentialOutput(index, {
                        provisioningInstructions: checked ? '' : undefined,
                      })
                    }
                    label="Account needs provisioning"
                    description="This account needs to be created/activated before first login"
                  />

                  {output.credentialOutput.provisioningInstructions !== undefined && (
                    <TextField
                      label="Provisioning Instructions"
                      aria-label="Provisioning instructions"
                      value={output.credentialOutput.provisioningInstructions || ''}
                      onChange={(value) =>
                        updateCredentialOutput(index, {
                          provisioningInstructions: value || undefined,
                        })
                      }
                      placeholder="e.g., Check inbox for invite email, click the invite link, complete registration"
                      description="Instructions for creating/activating the account before first login"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <Button variant="outline" size="sm" onPress={addOutput} iconLeft={<Plus size={14} />}>
        Add Output
      </Button>
    </div>
  );
}
